import { __awaiter, __generator, __read } from "tslib";
import '@office-start/style-utility/dist/styles/HomePage.scss';
import { FontIcon, Persona, PersonaSize } from '@fluentui/react';
import { ActionCommandMenu } from '@office-start/action-context-menu-control';
import { ActivityControl } from '@office-start/activity-control';
import { MenuItemTypes } from '@office-start/common-types';
import { ActionsMenuButton, RightClickActionsMenu, useActionsMenuRenderer, useActivityRenderer, } from '@office-start/document-control';
import DocumentIconControl from '@office-start/document-icon-control';
import { ActivityNumLines, FileType, } from '@office-start/document-types';
import { DropdownMenu, MenuSize, } from '@office-start/dropdown-menu';
import { IconNames } from '@office-start/fabric-icon-subset-utility';
import IconButtonControl from '@office-start/icon-button-control';
import { useTransformDateTimeString } from '@office-start/loc-service';
import { getOverflowIndex, getWidthArray, } from '@office-start/overflow-utility';
import { useSingletonWindowSize } from '@office-start/window-utility';
import classNames from 'classnames';
import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { format } from 'react-string-format';
import { Breadcrumb } from './Breadcrumb/Breadcrumb';
import styles from './EdgeworthItemControl.module.scss';
import { getHeaderAriaLabel } from './getHeaderAriaLabel';
import { getItemDisplayTime } from './getItemDisplayTime';
import { useCreateDocumentActions } from './hooks/useCreateDocumentActions';
import { usePerson } from './hooks/usePerson';
import { EdgeworthItemPersonControl } from './views/EdgeworthItemPersonControl';
var LazyTagGroupMenuControl = lazy(function () { return import('@office-start/tag-group-menu-control'); });
var ARTIFACT_DROPDOWN_WIDTH = 78;
export var EdgeworthItemControl = function (_a) {
    var _b;
    var _c;
    var item = _a.item, onAttributionClick = _a.onAttributionClick, onDocClick = _a.onDocClick, currentTab = _a.currentTab, dateResources = _a.dateResources, edgeworthItemResources = _a.edgeworthItemResources, edgeworthTaggingResources = _a.edgeworthTaggingResources, preferredCulture = _a.preferredCulture, createDocumentActionsOptions = _a.createDocumentActionsOptions, style = _a.style, getActivityPersonUrl = _a.getActivityPersonUrl, filterText = _a.filterText, PeopleCard = _a.PeopleCard, onActionMenuToggled = _a.onActionMenuToggled, onCommandMenuToggled = _a.onCommandMenuToggled, onMeetingArtifactClick = _a.onMeetingArtifactClick, hidePersonIcon = _a.hidePersonIcon, hideSharedByColumn = _a.hideSharedByColumn, hideEyeLash = _a.hideEyeLash, hideBreadCrumb = _a.hideBreadCrumb, _d = _a.quickAccessEnabled, quickAccessEnabled = _d === void 0 ? false : _d, edgeworthWidth = _a.edgeworthWidth, tagItems = _a.tagItems, onTagItemClick = _a.onTagItemClick, showTagsColumn = _a.showTagsColumn, msaUserHint = _a.msaUserHint;
    var displayTime = useMemo(function () { return getItemDisplayTime(item, currentTab); }, [currentTab, item]);
    var headerAriaLabel = useMemo(function () { return getHeaderAriaLabel(edgeworthItemResources, currentTab); }, [currentTab, edgeworthItemResources]);
    //Meeting artifact states
    //TODO: keyboarding for meeting artifacts
    var meetingArtifacts = useMemo(function () {
        return item.meetingObjects || [];
    }, [item.meetingObjects]);
    var displayDropdown = !!(item.meetingObjects &&
        edgeworthItemResources.meetingArtifactOverflowMenuTitle);
    var _f = __read(useState([]), 2), artifactWidthArray = _f[0], setArtifactWidthArray = _f[1];
    var _g = __read(useState(meetingArtifacts.length), 2), overflowIndex = _g[0], setOverflowIndex = _g[1];
    var _h = __read(useState(0), 2), meetingObjectContainerWidth = _h[0], setMeetingObjectContainerWidth = _h[1];
    var _j = __read(useState(0), 2), dropdownWidth = _j[0], setDropdownWidth = _j[1];
    //Meeting Artifact Refs
    var meetingArtifactContainerRef = useRef(null);
    var dropdownArtifactsRef = useRef(null);
    var meetingArtifactDropdownItems = useMemo(function () {
        return displayDropdown
            ? meetingArtifacts &&
                meetingArtifacts.slice(overflowIndex).map(function (artifact) { return ({
                    key: artifact.id,
                    content: (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: styles['meeting-artifact__dropdown-icon'] },
                            React.createElement(DocumentIconControl, { item: artifact, iconSize: "16x16", customIconAltText: edgeworthItemResources.customIconAltText })),
                        React.createElement("span", null, artifact.title))),
                    ariaLabel: artifact.title,
                }); })
            : [];
    }, [
        displayDropdown,
        edgeworthItemResources.customIconAltText,
        meetingArtifacts,
        overflowIndex,
    ]);
    //Calculate overflow index
    useLayoutEffect(function () {
        if (!displayDropdown ||
            !meetingArtifactContainerRef.current ||
            !artifactWidthArray ||
            artifactWidthArray.length === 0) {
            return;
        }
        setOverflowIndex(getOverflowIndex(meetingObjectContainerWidth, artifactWidthArray, dropdownWidth));
    }, [
        artifactWidthArray,
        displayDropdown,
        dropdownWidth,
        meetingArtifacts,
        meetingObjectContainerWidth,
        overflowIndex,
    ]);
    var onKeySelected = useCallback(function (key, event) {
        var selectedArtifact = meetingArtifacts.find(function (artifact) { return key === artifact.id; });
        if (selectedArtifact) {
            event.preventDefault();
            event.stopPropagation();
            onMeetingArtifactClick &&
                onMeetingArtifactClick(selectedArtifact);
        }
    }, [meetingArtifacts, onMeetingArtifactClick]);
    var artifactDropdown = useMemo(function () {
        var _a;
        var dropdownTitle = format(edgeworthItemResources.meetingArtifactOverflowMenuTitle, meetingArtifactDropdownItems.length.toString());
        return displayDropdown ? (React.createElement("span", { ref: dropdownArtifactsRef, className: classNames(styles['artifact-list__dropdown'], (_a = {},
                _a[styles['artifact-list__dropdown--hidden']] = dropdownWidth === 0,
                _a[styles['artifact-list__dropdown--none']] = dropdownWidth !== 0 &&
                    meetingArtifactDropdownItems.length === 0,
                _a)), role: "button", tabIndex: 0 },
            React.createElement(DropdownMenu, { buttonContent: React.createElement("span", { className: classNames(styles['artifact-overflow-menu'], 'ms-fcl-bl ms-bgc-nl-a') },
                    dropdownTitle,
                    React.createElement(FontIcon, { className: classNames('ms-fcl-b', //TODO: remove
                        styles['artifact-dropdown-chevron']), iconName: IconNames.ChevronDown })), items: meetingArtifactDropdownItems, options: { leftAligned: false, menuSize: MenuSize.Small }, menuItemType: MenuItemTypes.List, onKeySelected: onKeySelected, stopButtonEventPropagation: true }))) : null;
    }, [
        displayDropdown,
        dropdownWidth,
        edgeworthItemResources.meetingArtifactOverflowMenuTitle,
        meetingArtifactDropdownItems,
        onKeySelected,
    ]);
    useMemo(function () {
        if (edgeworthWidth) {
            // Subtract left and right padding
            var width = edgeworthWidth - 58 * 2;
            setMeetingObjectContainerWidth(width);
        }
        else {
            setMeetingObjectContainerWidth(0);
        }
    }, [edgeworthWidth]);
    useLayoutEffect(function () {
        if (!displayDropdown) {
            return;
        }
        //Hard code dropdown width
        if (dropdownArtifactsRef.current) {
            setDropdownWidth(ARTIFACT_DROPDOWN_WIDTH);
        }
        else {
            setDropdownWidth(0);
        }
        //Compute and store width occupied by a meeting artifact
        if (meetingArtifactContainerRef.current) {
            var widthArray = getWidthArray(meetingArtifactContainerRef.current, 'meeting-artifact__button');
            setArtifactWidthArray(widthArray);
        }
    }, [displayDropdown, meetingArtifacts]);
    var windowSize = useSingletonWindowSize();
    var shortDate = windowSize.width < 640; //TODO: add fabric width constants
    var transformToDateTimeString = useTransformDateTimeString(dateResources, preferredCulture);
    var dateTimeCellValue = transformToDateTimeString(displayTime, !!msaUserHint || shortDate, undefined, false, false);
    var personColumn = usePerson(item, currentTab, msaUserHint, edgeworthItemResources.youLabel);
    var componentRef = useRef(null);
    var actionsMenuRenderingPhase = useActionsMenuRenderer(componentRef);
    var activityRenderingPhase = useActivityRenderer();
    var _k = __read(useState(''), 2), activityPersonUrl = _k[0], setActivityPersonUrl = _k[1];
    //TODO: refactor to personPictureUtility
    useEffect(function () {
        var isMounted = true;
        var getUrl = function () { return __awaiter(void 0, void 0, void 0, function () {
            var url;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!getActivityPersonUrl) return [3 /*break*/, 2];
                        return [4 /*yield*/, getActivityPersonUrl(item)];
                    case 1:
                        url = _a.sent();
                        if (isMounted && url) {
                            setActivityPersonUrl(url);
                        }
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        }); };
        getUrl();
        return function () {
            isMounted = false;
        };
    }, [getActivityPersonUrl, item]);
    // TODO: Add attribution behavior
    var itemTitle = useMemo(function () {
        if (filterText) {
            try {
                var stringMatch = new RegExp(filterText, 'gi');
                var formatString = item.title.replace(stringMatch, '{0}');
                return format(formatString, React.createElement("b", null, filterText));
            }
            catch (_e) {
                /* do nothing */
            }
        }
        return item.title;
    }, [filterText, item.title]);
    var shouldRenderActivity = useMemo(function () { return !!item.activity && activityRenderingPhase; }, [activityRenderingPhase, item.activity]);
    var _l = useCreateDocumentActions(actionsMenuRenderingPhase, item, createDocumentActionsOptions), actionMenuItems = _l.actionMenuItems, commandItems = _l.commandItems, contentActionsEnabled = _l.contentActionsEnabled, contentActionsGroupInfo = _l.contentActionsGroupInfo;
    var onAuxClickCallback = useCallback(function (ev) {
        if (ev && ev.button === 1) {
            // Middle mouse button should open item in new tab. Only handle that.
            onDocClick(ev);
            ev.stopPropagation();
        }
    }, [onDocClick]);
    var personaElement = useMemo(function () {
        var _a, _b;
        if (shouldRenderActivity &&
            !hidePersonIcon &&
            ((_a = item.activity) === null || _a === void 0 ? void 0 : _a.people[0])) {
            return (React.createElement(Persona, { className: styles['activity-persona'], imageUrl: activityPersonUrl
                    ? "data:image/png;base64,".concat(activityPersonUrl)
                    : undefined, coinSize: 20, text: ((_b = item.activity) === null || _b === void 0 ? void 0 : _b.people[0].name) || '', hidePersonaDetails: true, size: PersonaSize.size32 }));
        }
        return null;
    }, [
        activityPersonUrl,
        shouldRenderActivity,
        hidePersonIcon,
        item.activity,
    ]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: componentRef, style: style, className: styles['list-item__body'], role: "rowgroup" },
            React.createElement("a", { href: (_c = item.url) !== null && _c !== void 0 ? _c : item.webUrl, onClick: onDocClick, onAuxClick: onAuxClickCallback, className: classNames(styles['document-list-item'], styles['document-list-item__padding']), "data-clicklog": "custom", role: "row" },
                React.createElement("div", { className: classNames(styles['centered-flex-container'], 'list-item__height-restrictor'), role: "none" },
                    React.createElement("div", { className: classNames(styles['document-column__name'], styles['list-item-column']), "aria-label": format(edgeworthItemResources.nameLabel, item.title), role: "gridcell" },
                        React.createElement("div", { className: styles['document-list-item__icon'] },
                            React.createElement(DocumentIconControl, { item: item, iconSize: "32x32", customIconAltText: edgeworthItemResources.customIconAltText })),
                        React.createElement("div", { className: "clip" },
                            React.createElement("div", { className: styles['centered-flex-container'] },
                                React.createElement("div", { className: classNames(styles['list-item__title']) }, itemTitle),
                                !hideEyeLash && item.isNew ? (React.createElement(FontIcon, { className: classNames(styles['is-new-icon'], 'ms-fcl-tda'), iconName: IconNames.Glimmer })) : null,
                                item.isShared && !quickAccessEnabled ? (React.createElement(FontIcon, { className: classNames(styles['shared-icon']), iconName: IconNames.People })) : null),
                            !hideBreadCrumb ? (React.createElement("div", { className: classNames(styles['list-item__secondary']) },
                                React.createElement(Breadcrumb, { item: item, transformToDateTimeString: transformToDateTimeString, onAttributionClick: onAttributionClick }))) : null),
                        contentActionsEnabled &&
                            actionsMenuRenderingPhase ? (React.createElement("div", { className: classNames(styles['list-item-column'], styles['column--commanding'], styles['column--commanding--hidden'], styles['document-column-padding']) },
                            React.createElement(ActionCommandMenu, { onCommandMenuToggled: onCommandMenuToggled, commandItems: commandItems, groupInfo: contentActionsGroupInfo }),
                            React.createElement(ActionsMenuButton, { item: item, actions: actionMenuItems, ariaLabel: edgeworthItemResources.moreActionsButtonAriaLabel, contentActionsEnabled: true, groupInfo: contentActionsGroupInfo, onActionMenuToggled: onActionMenuToggled, "data-clicklog": "custom" }))) : (React.createElement("div", { role: "button", className: classNames(styles['column--commanding'], styles['column--commanding--hidden']), tabIndex: 0 },
                            commandItems.length > 0 &&
                                actionsMenuRenderingPhase
                                ? commandItems.map(function (commandItem) { return (React.createElement("span", { key: commandItem.id, className: classNames(styles['commanding-item'], 'ms-bgc-g40-h ms-bgc-g40-a ms-bgc-g40-f') },
                                    React.createElement(IconButtonControl, { iconName: commandItem.icon || '', iconClass: "ms-fcl-nd", onClick: function (event) {
                                            if (commandItem.executeAction) {
                                                commandItem.executeAction(event);
                                            }
                                        } }))); })
                                : null,
                            actionMenuItems.length > 0 &&
                                actionsMenuRenderingPhase ? (React.createElement(ActionsMenuButton, { item: item, actions: actionMenuItems, ariaLabel: edgeworthItemResources.moreActionsButtonAriaLabel, buttonClasses: [
                                    styles['commanding-item'],
                                    styles['document-list-item__icon--menu'],
                                    styles['actions-menu-link'],
                                    'ms-fcl-np ms-bgc-g40-h',
                                ], "data-clicklog": "custom", onActionMenuToggled: onActionMenuToggled })) : null))),
                    showTagsColumn ? (React.createElement("div", { className: classNames(styles['list-item-column'], (_b = {},
                            _b[styles['document-column__tag']] = !quickAccessEnabled,
                            _b[styles['document-column__qa_tag']] = quickAccessEnabled,
                            _b)), role: "gridcell" }, tagItems ? (React.createElement(Suspense, { fallback: null },
                        React.createElement(LazyTagGroupMenuControl, { tagItems: tagItems, onTagItemClick: onTagItemClick, tagGroupAriaLabel: format(edgeworthTaggingResources.tagGroupAriaLabel, tagItems.length.toString()) }))) : null)) : null,
                    React.createElement("div", { className: classNames(styles['list-item-column'], styles['document-column__date']), role: "gridcell", "aria-label": format(headerAriaLabel.dateLabel, dateTimeCellValue ? dateTimeCellValue : '') },
                        React.createElement("span", { className: styles['document-column-padding'] }, dateTimeCellValue)),
                    !hideSharedByColumn ? (React.createElement(EdgeworthItemPersonControl, { person: personColumn, PeopleCard: PeopleCard, personHeaderAriaLabel: format(headerAriaLabel.personLabel, personColumn ? personColumn.name : '') })) : null,
                    React.createElement("div", { className: classNames(styles['list-item-column'], styles['document-column__activity']), role: "gridcell", "aria-label": format(edgeworthItemResources.activityLabel, item.activity ? item.activity.plainText : '') }, shouldRenderActivity ? (React.createElement("div", { className: styles['activity-wrapper'] },
                        personaElement ? (React.createElement(React.Fragment, null, PeopleCard ? (React.createElement(PeopleCard, { upn: item.activity.people[0]
                                .upn }, personaElement)) : (personaElement))) : null,
                        React.createElement(ActivityControl, { formatString: item.activity.formatString, numLines: ActivityNumLines.One, plainText: item.activity.plainText, people: item.activity.people, reasonType: item.activity.reasonType, timestamp: item.activity.timestamp, smallIcon: true, id: item.activity.id, dateResources: dateResources, preferredCulture: preferredCulture, PeopleCard: PeopleCard }))) : null)),
                item.fileType === FileType.MeetingObject &&
                    meetingArtifacts &&
                    meetingArtifacts.length > 0 ? (React.createElement("div", { ref: meetingArtifactContainerRef, className: styles['meeting-artifacts-container'] },
                    meetingArtifacts
                        .slice(0, overflowIndex)
                        .map(function (meetingArtifact) { return (React.createElement("button", { key: meetingArtifact.id, title: meetingArtifact.title, className: styles['meeting-artifact__button'], onClick: function (event) {
                            event.preventDefault();
                            event.stopPropagation();
                            onMeetingArtifactClick &&
                                onMeetingArtifactClick(meetingArtifact);
                        }, "data-clicklog": "custom" },
                        React.createElement("div", { className: styles['meeting-artifact__icon'] },
                            React.createElement(DocumentIconControl, { item: meetingArtifact, iconSize: "20x20", customIconAltText: edgeworthItemResources.customIconAltText })),
                        React.createElement("span", { className: styles['meeting-artifact__title'] }, meetingArtifact.title))); }),
                    artifactDropdown)) : null)),
        actionMenuItems.length > 0 && actionsMenuRenderingPhase ? (React.createElement(RightClickActionsMenu, { actions: actionMenuItems, componentRef: componentRef, contentActionsEnabled: contentActionsEnabled, groupInfo: contentActionsGroupInfo, item: item, onActionMenuToggled: onActionMenuToggled })) : null));
};
//# sourceMappingURL=EdgeworthItemControl.js.map